/**
 * Admin Dashboard v2 - MaintainX Style
 * Clean, enterprise-grade design with dark sidebar
 */

import { Routes, Route, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import MaintainXLayout from "@/components/layouts/MaintainXLayout";
import PayablesDueAlert from "@/components/admin/PayablesDueAlert";
import PayablesDeskPage from "@/pages/admin/PayablesDeskPage";
import DashboardOverview from "@/components/dashboard/DashboardOverview";
import AccessRequestsWidget from "@/components/AccessRequestsWidget";
import CampaignAlertsPanel from "@/components/CampaignAlertsPanel";
import CampaignOrdersManagement from "@/components/admin/CampaignOrdersManagement";
import SettingsPage from "@/pages/SettingsPage";
import { toast } from "sonner";

import BackorderPipeline from "@/components/BackorderPipeline";

// Import existing admin components
import ProjectManagement from "@/pages/ProjectManagementPage";
import BidDeskPage from "@/pages/BidDeskPage";
import CrewSchedulePage from "@/pages/CrewSchedulePage";
import AssetRegistry from "@/components/admin/AssetRegistry";
import MaintenanceManagement from "@/components/admin/MaintenanceManagement";
import AdminWorkOrdersBoard from "@/components/admin/AdminWorkOrdersBoard";
import WorkOrderDetailPage from "@/pages/WorkOrderDetailPage";
import BackordersManagement from "@/components/admin/BackordersManagement";
import AdminBackordersBoard from "@/components/admin/AdminBackordersBoard";
import TrainingCenterPage from "@/pages/TrainingCenterPage";
import TrainingProgressCard from "@/components/training/TrainingProgressCard";
import GuidedWalkthrough from "@/components/training/GuidedWalkthrough";
import ContractorManagement from "@/components/admin/ContractorManagement";
import BillingManagement from "@/components/admin/BillingManagement";
import SecurityDashboard from "@/components/admin/SecurityDashboard";
import UserManagement from "@/components/admin/UserManagement";
import OrdersManagement from "@/components/admin/OrdersManagement";
import AdminOrdersBoard from "@/components/admin/AdminOrdersBoard";
import UnifiedProjectManagement from "@/components/admin/UnifiedProjectManagement";
import HospitalManagement from "@/components/admin/HospitalManagement";
import SupplierManagement from "@/components/admin/SupplierManagement";
import AuditLogDashboard from "@/components/admin/AuditLogDashboard";
import AnalyticsDashboard from "@/components/admin/AnalyticsDashboard";
import EmailCampaigns from "@/components/admin/EmailCampaigns";
import TradeIntelligence from "@/components/admin/TradeIntelligence";
import AdminMarketplace from "@/components/admin/AdminMarketplace";
import SolMPriceAdmin from "@/components/admin/SolMPriceAdmin";
import MicroCarePriceAdmin from "@/components/admin/MicroCarePriceAdmin";
import AdminGlovesPricing from "@/pages/AdminGlovesPricing";
import AdminAnsellDatasheets from "@/pages/AdminAnsellDatasheets";
import LettersManagement from "@/components/admin/LettersManagement";
import AdminEnvironmentalTesting from "@/components/admin/AdminEnvironmentalTesting";
import QuickBooksIntegration from "@/components/admin/QuickBooksIntegration";
import CourierJobsManagement from "@/components/admin/CourierJobsManagement";
import RegistrationLeads from "@/components/admin/RegistrationLeads";
import ExecutiveSummaryGenerator from "@/components/admin/ExecutiveSummaryGenerator";
import DualRoleManagement from "@/components/admin/DualRoleManagement";
import InvestorLeads from "@/components/admin/InvestorLeads";
import BioburdenVisitorsPanel from "@/components/admin/BioburdenVisitorsPanel";
import PhilanthropyPage from "@/pages/admin/PhilanthropyPage";
import ProposalsPanel from "@/components/admin/ProposalsPanel";
import DITankManagement from "@/components/admin/DITankManagement";
import QuickNotes from "@/components/admin/QuickNotes";
import SupplierOrdersManager from "@/components/admin/SupplierOrdersManager";
import GrossProfitBoard from "@/components/admin/GrossProfitBoard";
import SupplierSpendBoard from "@/components/admin/SupplierSpendBoard";
import SupplierPayablesDesk from "@/components/admin/SupplierPayablesDesk";
import SupplierScorecardBoard from "@/components/admin/SupplierScorecardBoard";
import CommissionsManager from "@/components/admin/CommissionsManager";
import CrmManager from "@/components/admin/CrmManager";
import TeamApprovalsPage from "@/pages/admin/TeamApprovalsPage";
import DeveloperNotesPage from "@/pages/admin/DeveloperNotesPage";
import DemoPresentationsPage from "@/pages/admin/DemoPresentationsPage";
import CapitalQuotePage from "./CapitalQuotePage";
import ConstructionProposalPage from "./ConstructionProposalPage";
import ConstructionProposalsPanel from "@/components/admin/ConstructionProposalsPanel";
import AdminLiveInventoryUpload from "@/components/admin/AdminLiveInventoryUpload";
import TeamApprovalsWidget from "@/components/admin/TeamApprovalsWidget";

// Page title mapping
const pageTitles = {
  "/admin": { title: "Dashboard", subtitle: "Overview of your facility operations" },
  "/admin/maintenance": { title: "Work Orders", subtitle: "Manage maintenance requests" },
  "/admin/project-management": { title: "Project Management", subtitle: "PM schedules and construction projects" },
  "/admin/bid-desk": { title: "Bid Desk", subtitle: "Agency solicitations → subcontractor RFQ packets" },
  "/admin/crew-schedule": { title: "Crew Schedule & Dispatch", subtitle: "Week board — drag jobs, dispatch crews" },
  "/admin/proposals": { title: "Proposals", subtitle: "Capital-project quotes & contracted proposals — create, edit, duplicate, preview & download PDFs" },
  "/admin/assets": { title: "Assets", subtitle: "Equipment and asset registry" },
  "/admin/orders": { title: "Orders", subtitle: "Marketplace orders & quote-cart submissions — track status, communications, tracking #" },
  "/admin/backorders": { title: "Sourcing Desk", subtitle: "RFQ → quote → award for backorders and maintenance & repair work orders" },
  "/admin/payables": { title: "Payables Desk", subtitle: "Invoice audit, disbursements and supplier PO reconciliation — everything the Sourcing Desk pays out" },
  "/admin/backorders/pipeline": { title: "Sourcing Pipeline", subtitle: "Visual workflow management" },
  "/admin/backorders/live-inventory": { title: "Live Inventory Uploader", subtitle: "Upload the daily Hard-to-Find Live Backorder Inventory list — public page at /backorders/live-inventory" },
  "/admin/contractors": { title: "Contractors", subtitle: "Manage contractor network" },
  "/admin/suppliers": { title: "Suppliers", subtitle: "Manage supplier relationships" },
  "/admin/hospitals": { title: "Hospitals", subtitle: "Connected health systems" },
  "/admin/users": { title: "Users", subtitle: "Team members and permissions" },
  "/admin/analytics": { title: "Analytics", subtitle: "Performance metrics and insights" },
  "/admin/environmental": { title: "Water Testing", subtitle: "Environmental compliance tracking" },
  "/admin/audit-log": { title: "Audit Log", subtitle: "System activity history" },
  "/admin/security": { title: "Security", subtitle: "Security settings and logs" },
  "/admin/settings": { title: "Settings", subtitle: "Notifications, integrations, and account settings" },
  "/admin/marketplace": { title: "Marketplace", subtitle: "Product catalog and order management" },
  "/admin/solm-pricing": { title: "Sol-M Pricing", subtitle: "Manual price entry for Sol-Millennium SKUs (super-admin)" },
  "/admin/microcare-pricing": { title: "MicroCare Pricing", subtitle: "Sterile Processing / Instrument Reprocessing catalog + prices (super-admin)" },
  "/admin/philanthropy": { title: "Philanthropy", subtitle: "Isn't That Dandy! - Cultural health gala initiative" },
  "/admin/di-tanks": { title: "DI Tank Management", subtitle: "Deionized water tank deployments and exchange scheduling" },
  "/admin/quick-notes": { title: "Quick Notes", subtitle: "Track requests, follow-ups, and meeting notes" },
  "/admin/supplier-orders": { title: "Supplier Orders", subtitle: "Track open orders by supplier — Lux Medical, Eviva, Getinge, etc." },
  "/admin/supplier-scorecard": { title: "Supplier Scorecard", subtitle: "Who answers an RFQ, who wins the work, and who ships on the date they promised" },
  "/admin/gross-profit": { title: "Gross Profit", subtitle: "Revenue against supplier cost — daily, weekly, monthly, quarterly, and matched to QuickBooks" },
  "/admin/supplier-spend": { title: "Supplier Spend & Margin", subtitle: "Admin-only rollup of per-supplier spend, revenue, and markup — spot cost creep and negotiate rebates" },
  "/admin/payables-desk": { title: "Supplier Payables", subtitle: "Every supplier invoice and payment — including historical records keyed in by hand for reconciliation" },
  "/admin/commissions": { title: "Commissions", subtitle: "Sales reps & commission tracking — gross-profit based" },
  "/admin/crm": { title: "CRM", subtitle: "Customers, leads, activities & sales pipeline" },
  "/admin/team-approvals": { title: "Team Portal", subtitle: "Invite employees, manage roles, and track team access (admins only)" },
  "/admin/trade-intelligence": { title: "Trade Intelligence", subtitle: "US sea-import records — find consignees & overseas suppliers" },
  "/admin/developer-notes": { title: "Developer Notes", subtitle: "Guided tour of the Sterile Mate tech stack — read-only reviewer view" },
  "/admin/demo-presentations": { title: "Demo Presentations", subtitle: "Customer-ready decks staged for meetings" },

  "/admin/training": { title: "Training & Tutorials", subtitle: "Narrated walkthroughs, live guides and printable quick reference cards" },
  "/admin/bioburden-visitors": { title: "Bioburden Visitors", subtitle: "Every hospital contact who unlocked the /services/bioburden landing page" },
};

// Dashboard content wrapper
const DashboardContent = () => {
  const { user, token } = useAuth();

  return (
    <div className="space-y-6">
      <DashboardOverview />

      <TrainingProgressCard />

      {/* Supplier Campaign Alerts - For all admins */}
      <CampaignAlertsPanel
        token={token}
        userType="admin"
        maxAlerts={5}
        onAlertClick={(alert) => {
          toast.info(`Campaign from ${alert.supplier_name}: ${alert.product_name} - $${alert.unit_price?.toFixed(2)}/unit`);
        }}
      />

      {/* Campaign Quote Requests Management */}
      <CampaignOrdersManagement token={token} />

      {/* Access Requests Widget - Admin Only */}
      {["super_admin", "hub_admin"].includes(user?.role) && (
        <>
          <TeamApprovalsWidget />
          <AccessRequestsWidget />
        </>
      )}
    </div>
  );
};

// Small wrapper — pulls the auth token and hands it to the uploader panel.
const LiveInventoryPanel = () => {
  const { token } = useAuth();
  return <AdminLiveInventoryUpload token={token} />;
};

// Wrapper — Proposals sidebar entry: shows the Capital Quotes list as a
// standalone page so Project Managers can jump straight to it.
const ProposalsPage = () => {
  const { token } = useAuth();
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <span className="inline-block w-1 h-6 rounded bg-blue-600" />
          Proposals & Capital Quotes
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Create new proposals, duplicate an existing one, or download the latest server-rendered PDF.
        </p>
      </div>
      <ConstructionProposalsPanel token={token} />
      <ProposalsPanel token={token} />
    </div>
  );
};

export default function AdminDashboardV2() {
  const location = useLocation();
  const { token } = useAuth();
  const pageInfo = pageTitles[location.pathname] || { title: "Admin", subtitle: "" };

  return (
    <MaintainXLayout title={pageInfo.title} subtitle={pageInfo.subtitle}>
      <PayablesDueAlert token={token} />
      <GuidedWalkthrough />
      <Routes>
        <Route path="/" element={<DashboardContent />} />
        <Route path="/training" element={<TrainingCenterPage />} />
        <Route path="/maintenance" element={<AdminWorkOrdersBoard />} />
        <Route path="/maintenance/legacy" element={<MaintenanceManagement />} />
        <Route path="/work-orders" element={<AdminWorkOrdersBoard />} />
        <Route path="/work-orders/:id" element={<WorkOrderDetailPage />} />
        <Route path="/project-management" element={<UnifiedProjectManagement />} />
        <Route path="/proposals" element={<ProposalsPage />} />
        <Route path="/proposals/quote/:id" element={<CapitalQuotePage />} />
        <Route path="/proposals/construction/:id" element={<ConstructionProposalPage />} />
        <Route path="/assets" element={<AssetRegistry />} />
        <Route path="/orders" element={<AdminOrdersBoard />} />
        <Route path="/orders/legacy" element={<OrdersManagement />} />
        <Route path="/backorders" element={<AdminBackordersBoard />} />
        <Route path="/payables" element={<PayablesDeskPage />} />
        <Route path="/backorders/legacy" element={<BackordersManagement />} />
        <Route path="/backorders/pipeline" element={<BackorderPipeline viewMode="admin" />} />
        <Route path="/backorders/live-inventory" element={<LiveInventoryPanel />} />
        <Route path="/contractors" element={<ContractorManagement />} />
        <Route path="/suppliers" element={<SupplierManagement />} />
        <Route path="/hospitals" element={<HospitalManagement />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/analytics" element={<AnalyticsDashboard />} />
        <Route path="/environmental" element={<AdminEnvironmentalTesting />} />
        <Route path="/audit-log" element={<AuditLogDashboard />} />
        <Route path="/security" element={<SecurityDashboard />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/projects/*" element={<ProjectManagement />} />
        <Route path="/bid-desk" element={<BidDeskPage />} />
        <Route path="/crew-schedule" element={<CrewSchedulePage />} />
        <Route path="/billing" element={<BillingManagement />} />
        <Route path="/email-campaigns" element={<EmailCampaigns />} />
        <Route path="/trade-intelligence" element={<TradeIntelligence />} />
        <Route path="/letters" element={<LettersManagement />} />
        <Route path="/quickbooks" element={<QuickBooksIntegration />} />
        <Route path="/courier-jobs" element={<CourierJobsManagement />} />
        <Route path="/registration-leads" element={<RegistrationLeads />} />
        <Route path="/executive-summary" element={<ExecutiveSummaryGenerator />} />
        <Route path="/dual-role" element={<DualRoleManagement />} />
        <Route path="/investor-leads" element={<InvestorLeads />} />
        <Route path="/bioburden-visitors" element={<BioburdenVisitorsPanel />} />
        <Route path="/marketplace" element={<AdminMarketplace />} />
        <Route path="/solm-pricing" element={<SolMPriceAdmin />} />
        <Route path="/microcare-pricing" element={<MicroCarePriceAdmin />} />
        <Route path="/gloves-pricing" element={<AdminGlovesPricing />} />
        <Route path="/ansell-datasheets" element={<AdminAnsellDatasheets />} />
        <Route path="/philanthropy" element={<PhilanthropyPage />} />
        <Route path="/di-tanks" element={<DITankManagement />} />
        <Route path="/quick-notes" element={<QuickNotes />} />
        <Route path="/supplier-orders" element={<SupplierOrdersManager />} />
        <Route path="/supplier-spend" element={<SupplierSpendBoard token={token} />} />
        <Route path="/payables-desk" element={<SupplierPayablesDesk token={token} />} />
        <Route path="/gross-profit" element={<GrossProfitBoard />} />
        <Route path="/supplier-scorecard" element={<SupplierScorecardBoard token={token} />} />
        <Route path="/commissions" element={<CommissionsManager />} />
        <Route path="/crm" element={<CrmManager scope="admin" />} />
        <Route path="/team-approvals" element={<TeamApprovalsPage />} />
        <Route path="/developer-notes" element={<DeveloperNotesPage />} />
        <Route path="/demo-presentations" element={<DemoPresentationsPage />} />
      </Routes>
    </MaintainXLayout>
  );
}
